import styled from "styled-components";
import { theme } from "../styles/theme";

// Styled Components
export const PageContainer = styled.main`
  min-height: 100vh;
  background: transparent;
  position: relative;
  overflow: hidden;
  padding: 1rem 0;

  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: 2rem 0 0;
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0;
  }
  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 0.03) 0%,
      transparent 80%
    );
    animation: pulse 15s ease-in-out infinite;
  }

  @keyframes pulse {
    0% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-5%, -5%);
    }
    100% {
      transform: translate(0, 0);
    }
  }
`;

export const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 10;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 0;
  }
`;

export const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
`;

export const Title = styled.h1`
  font-size: 1.5rem;

  @media (min-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }

  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  font-weight: 800;
  margin-bottom: 1rem;
  color: ${theme.colors.textDark};
  letter-spacing: -0.02em;
  text-align: center;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.1);
  animation: fadeIn 0.8s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  max-width: 100%;
  padding: 0 1rem;

  @media (min-width: ${theme.breakpoints.mobile}) {
    max-width: 32rem;
    padding: 0;
  }
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  animation: fadeIn 0.8s ease-out 0.2s both;
`;

export const SearchForm = styled.form`
  margin-bottom: 2.5rem;
  display: flex;
  gap: 0.5rem;
  max-width: 100%;
  padding: 0 1rem;
  flex-direction: column;

  @media (min-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    max-width: 36rem;
    padding: 0;
  }
  margin-left: auto;
  margin-right: auto;
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
`;

export const SearchInput = styled.input`
  width: 100%;
  font-family: ${theme.typography.fontFamily};
  padding: 0.75rem 1rem 0.75rem 2.5rem;

  @media (min-width: ${theme.breakpoints.mobile}) {
    padding: 1rem 1.25rem 1rem 3rem;
  }
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 20px rgba(128, 0, 255, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const SearchButton = styled.button`
  background: linear-gradient(
    135deg,
    rgba(128, 0, 255, 0.8) 0%,
    rgba(60, 0, 255, 0.8) 100%
  );
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: 0.75rem 1rem;
  width: 100%;

  @media (min-width: ${theme.breakpoints.mobile}) {
    padding: 1rem 1.5rem;
    width: auto;
  }
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 15px rgba(128, 0, 255, 0.2);

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(128, 0, 255, 0.9) 0%,
      rgba(60, 0, 255, 0.9) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(128, 0, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(128, 0, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const ResultsTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

export const ResultsList = styled.div`
  max-height: 63vh;
  overflow-y: auto;
  padding-right: 0.5rem;

  /* Ensure it does not collapse */
  display: block;
  position: relative;

  /* Optional but safer in constrained layouts */
  min-height: 0;

  /* Scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary}80 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.primary}80;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${theme.colors.primary};
  }
`;

export const ResultCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  margin-bottom: 1rem;

  @media (min-width: ${theme.breakpoints.mobile}) {
    padding: 1.25rem;
  }
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(128, 0, 255, 0.1) 0%,
      transparent 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 255, 255, 0.2);

    &::before {
      opacity: 1;
    }
  }
`;

export const ResultTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0 0 0.5rem 0;
`;
export const ResultSummary = styled.h4`
  font-size: 0.8rem;
  font-weight: 400;
  margin: 0 0 0.5rem 0;
`;

export const EmptyState = styled.div`
  text-align: center;
  color: ${theme.colors.textMuted};
  background: none;
  backdrop-filter: blur(8px);
  padding: 2rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.borderPrimary};
`;
export const ClearButton = styled.div`
  font-size: 1rem;
  font-weight: 400;
  margin-left: 0.2rem;
  cursor: pointer;
  display: flex;
  /* justify-content: center; */
  align-items: center;
`;
